import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import { ISegmentDatasourceColumnState } from 'src/app/dashboards/common/items/filter/segment-filter/segments';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import {
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import {
    multipleSelectComponentCompileSQLTokens,
    segmentContainerIdCompileSQLTokens,
} from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';
import { BQSqlFunctionHelper } from 'src/app/utils/datasource-sql-map/bq-sql-function-helper';

export class BiVizDashboardEngagement {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_engagement',
        columns: {
            ACTIVITY_DATE: {
                columnId: 'ACTIVITY_DATE',
                columnSQL: 'start_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'start_date',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION: {
                columnId: 'ACTIVITY_APP_VERSION',
                columnSQL: 'app_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'app_version',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION_GROUP_MINOR: {
                columnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['app_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['app_version']
                        )}.minor`,
                        state
                    );
                },
            },
            FIREBASE_FIRST_OPEN_DATE: {
                columnId: 'FIREBASE_FIRST_OPEN_DATE',
                columnSQL: 'player_dim.revenue_sessions.first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'player_dim.revenue_sessions.first_session_date',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'player_dim.revenue_sessions.first_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.first_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'player_dim.geo.first_geo.country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.country_tier_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'player_dim.geo.first_geo.unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.unified_country',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_US_REGION: {
                columnId: 'FIREBASE_COUNTRY_US_REGION',
                columnSQL: 'player_dim.geo.first_geo.us_subdivision',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.geo.first_geo.us_subdivision',
                        state
                    );
                },
            },
            ATTRIBUTION_TRAFFIC_SOURCE: {
                columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                columnSQL: 'player_dim.attribution.unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_traffic',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION: {
                columnId: 'FIREBASE_INSTALL_VERSION',
                columnSQL: 'player_dim.revenue_sessions.install_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.install_version',
                        state
                    );
                },
            },
            FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
                columnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                columnSQL: `${BQSqlFunctionHelper.getCallSqlStatement(
                    BQSqlFunctionHelper.VERSION_HIERARCHY,
                    ['player_dim.revenue_sessions.install_version']
                )}.minor`,
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        `${BQSqlFunctionHelper.getCallSqlStatement(
                            BQSqlFunctionHelper.VERSION_HIERARCHY,
                            ['player_dim.revenue_sessions.install_version']
                        )}.minor`,
                        state
                    );
                },
            },
            ETL_PAYER: {
                columnId: 'ETL_PAYER',
                columnSQL: 'player_dim.revenue_sessions.payer_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.payer_label',
                        state
                    );
                },
            },
            ETL_REVENUE_BADGE: {
                columnId: 'ETL_REVENUE_BADGE',
                columnSQL: 'player_dim.revenue_sessions.revenue_badge',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.revenue_sessions.revenue_badge',
                        state
                    );
                },
            },
            ATTRIBUTION_NETWORK_NAME: {
                columnId: 'ATTRIBUTION_NETWORK_NAME',
                columnSQL: 'player_dim.attribution.unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_network',
                        state
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_NAME: {
                columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                columnSQL: 'player_dim.attribution.unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'player_dim.attribution.unified_campaign',
                        state
                    );
                },
            },
            SEGMENT: {
                columnId: 'SEGMENT',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
            SEGMENT_EXCLUDE: {
                columnId: 'SEGMENT_EXCLUDE',
                columnSQL: 'segments',
                compileSQLTokens: segmentContainerIdCompileSQLTokens,
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const activityDateColumnSql =
            BiVizDashboardEngagement.sqlMap.columns.ACTIVITY_DATE?.columnSQL!;
        const dateTrunc = queryContextSettings.dateAggregation
            ? sqlDateTrunc(
                  activityDateColumnSql,
                  queryContextSettings.dateAggregation
              )
            : `${activityDateColumnSql}`;

        // if app_version is present in QueryContext
        const is_in_context_app_version =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'ACTIVITY_APP_VERSION'
            ) != -1;

        // if segment_container_id is present in splits or filters
        const is_in_context_segment =
            supportedDatasourceColumns.findIndex(
                (dsCol) => dsCol.id === 'SEGMENT'
            ) != -1;

        const sqlTokensArray: Partial<ICompiledSQLTokens>[] =
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const countStarOrDistinctProperId = splitSelect
            ? 'COUNT(DISTINCT proper_id)'
            : 'COUNT(*)';
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        const join: string = sqlTokens.join.join('\n');
        const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        return {
            dateTrunc,
            splitSelect,
            splitGroupBy,
            is_in_context_segment,
            countStarOrDistinctProperId,
            where,
            windowTotal,
            windowRunning,
            join,
        };
    }
}
